import React from "react";
import { useAuth } from "react-oidc-context";
import { ThreeDots } from "react-loader-spinner";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import { SuccessCheckIcon } from "../../../icons/SuccessCheckIcon";
import ClockIcon from "../../../icons/ClockIcon";
import { IDestinationExtension, IQuoteExtension } from "../../../types/IQuotes";
import { useNitroStore } from "../../../store/Store";
import NitroService from "../../../utils/nitro.service";
import { MissingTruckFreightDesktopSections } from "./multiLocationQuoteUtils";
import NitroRequestService from "../../../utils/nitroRequest.service";
import { icons } from "../../../svg";
import { useStickyTableHeader } from "../../../hooks/useStickyTableHeader";

interface TruckMissingOriginsTableProps {
  rows: TableRow[];
  loading: boolean;
  destination: IDestinationExtension;
}

type RowMilesStatus = "check" | "request" | "requested" | "dash";

export interface TableRow {
  rowKey: string;
  originName: string;
  milesStatus: RowMilesStatus;
  noGuidance: string | null;
  quote: IQuoteExtension;
}

// TODO: what do we do in case of multiple non koch occupants
// TODO: origin details are NOT always present...
// TODO: truck rows refresh all the time
// TODO: logistics tab doesn't save result
// TODO: new api details for logistics panel
// TODO: requested state doesn't persist on refresh
export const buildRows = (
  sections: MissingTruckFreightDesktopSections,
): TableRow[] => {
  const rows: TableRow[] = [];

  sections.noRouteOrigins.forEach((q) => {
    rows.push({
      rowKey: `no-route-${q.competitor ? "comp" : "koch"}-${q.origin.id}`,
      originName: q.origin.name,
      milesStatus: "dash",
      noGuidance: "No Route",
      quote: q,
    });
  });

  sections.missingMilesOrigins.forEach(({ quote: q, mileageRequested }) => {
    rows.push({
      rowKey: `missing-miles-${q.competitor ? "comp" : "koch"}-${q.origin.id}`,
      originName: q.origin.name,
      milesStatus: mileageRequested ? "requested" : "request",
      noGuidance: null,
      quote: q,
    });
  });

  sections.expiredOrigins.forEach((q) => {
    rows.push({
      rowKey: `no-guidance-${q.competitor ? "comp" : "koch"}-${q.origin.id}`,
      originName: q.origin.name,
      milesStatus: "check",
      noGuidance: "Expired",
      quote: q,
    });
  });

  sections.outsideMileageBandOrigins.forEach((data) => {
    rows.push({
      rowKey: `outside-mileage-${data.quote.competitor ? "comp" : "koch"}-${
        data.quote.origin.id
      }`,
      originName: data.quote.origin.name,
      milesStatus: "check",
      noGuidance: `Outside Mileage Band\n${data.mileage} mi`,
      quote: data.quote,
    });
  });

  return rows;
};

export const TruckMissingOriginsTable: React.FC<
  TruckMissingOriginsTableProps
> = ({ rows, loading, destination }) => {
  const [product] = useNitroStore((state) => [state.product]);
  const auth = useAuth();
  const { ref: shellRef, isSticky } = useStickyTableHeader(
    "deskstop-destination-table",
  );
  const [requestedMileageRows, setRequestedMileageRows] = React.useState<
    Set<string>
  >(new Set());
  const [loadingMileageRows, setLoadingMileageRows] = React.useState<
    Set<string>
  >(new Set());

  const renderMilesCell = (
    status: RowMilesStatus,
    quote: IQuoteExtension,
    rowKey: string,
  ) => {
    if (status === "check") return <SuccessCheckIcon />;
    if (status === "requested") {
      return (
        <span className={styles.truckMissingOriginsRequestedPill}>
          <ClockIcon className={styles.truckMissingOriginsRequestedPillIcon} />
          <span>Requested</span>
        </span>
      );
    }
    if (status === "request") {
      if (requestedMileageRows.has(rowKey)) {
        return (
          <span className={styles.truckMissingOriginsRequestedPill}>
            <ClockIcon
              className={styles.truckMissingOriginsRequestedPillIcon}
            />
            <span>Requested</span>
          </span>
        );
      }
      if (loadingMileageRows.has(rowKey)) {
        return <ThreeDots height="20" width="30" color="#4fa94d" />;
      }
      return (
        <button
          type="button"
          className={styles.truckMissingOriginsPill}
          onClick={async () => {
            setLoadingMileageRows((prev) => new Set([...prev, rowKey]));
            try {
              const destinationZip =
                destination.details?.zipCode || destination.zipCode || "";
              await NitroRequestService.postMileageRequest({
                marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
                product,
                mode: "TRUCK",
                origin: quote.origin.name,
                originZipCode: quote.origin.details?.zipCode || "",
                destination: destination.name,
                destinationZipCode: destinationZip,
                comments: "",
              });
              await NitroService.updateMileageRequestStatus({
                originZip: quote.origin.details?.zipCode || "",
                destinationZip,
                user: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
              });
              setRequestedMileageRows((prev) => new Set([...prev, rowKey]));
            } catch (error) {
              console.error(
                "Requests/missingMileageRequest - Failed to send email:",
                error,
              );
            } finally {
              setLoadingMileageRows((prev) => {
                const next = new Set(prev);
                next.delete(rowKey);
                return next;
              });
            }
          }}
          aria-label="Request Miles"
        >
          <span className={styles.truckMissingOriginsPillPlus}>+</span>
          <span>Request</span>
        </button>
      );
    }
    return <span className={styles.truckMissingOriginsDash}>—</span>;
  };

  return (
    <div
      ref={shellRef}
      className={styles.truckMissingOriginsTableShell}
      style={{ borderRadius: isSticky ? "0 0 14px 14px" : "14px" }}
    >
      {/* Table header row */}
      <div className={styles.truckMissingOriginsTableHeader}>
        <div className={styles.truckMissingOriginsColOrigin}>ORIGIN</div>
        <div className={styles.truckMissingOriginsColMiles}>MILES</div>
        <div className={styles.truckMissingOriginsColNoGuidance}>
          NO GUIDANCE
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={styles.truckMissingOriginsRow}>
          <span className={styles.truckMissingOriginsDash}>Loading…</span>
        </div>
      )}

      {/* Data rows */}
      {!loading &&
        rows.map((row) => (
          <div key={row.rowKey} className={styles.truckMissingOriginsRow}>
            {/* Origin */}
            <div className={styles.truckMissingOriginsColOrigin}>
              <div className={styles.truckMissingOriginsCityCell}>
                <img
                  src={
                    row.quote.competitor ? icons.RedDotIcon : icons.BlueDotIcon
                  }
                  alt="Dot Icon"
                  className={styles.dotIcon}
                />
                <span
                  id={`missing-truck-origin-name-${row.rowKey}`}
                  className={`${styles.originName} ${
                    row.quote.competitor
                      ? styles.originNameRed
                      : styles.originNameBlue
                  }`}
                >
                  {row.originName}
                </span>
              </div>
            </div>

            {/* Miles */}
            <div className={styles.truckMissingOriginsColMiles}>
              {renderMilesCell(row.milesStatus, row.quote, row.rowKey)}
            </div>

            {/* No Guidance */}
            <div className={styles.truckMissingOriginsColNoGuidance}>
              {row.noGuidance ? (
                <span className={styles.truckMissingOriginsStatusText}>
                  {row.noGuidance}
                </span>
              ) : (
                <span className={styles.truckMissingOriginsDash}>—</span>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};
